import { call, put, select, takeLatest, take, delay } from "redux-saga/effects";
import MiniListStore from "MiniListStore";
import SettingsStore from "SettingsStore";
import ConsoleStore from 'ConsoleStore';
import { AiInsightsConstants } from "../../Constants/AiInsightsConstants";
import aiInsightsApi from "../../ServiceApi/Apis/AiInsightsApi";
import { getAiInsightsStates, getDatagraphStates, symbolSelect } from "../../Reducers/NavDataGraph/TabDataGraph/selectors";
import baseServiceApi from "../../ServiceApi/BaseServiceApi";
import GetAiInsightsSelectedReportDataRequest from "../../ServiceApi/Requests/GetAiInsightsSelectedReportDataRequest";
import { eventChannel } from "redux-saga";
import { SettingsConstants } from "../../Constants/SettingsConstants";
import UserInfoUtil from "UserInfoUtil";
import TabType from "../../Constants/TabType";
import datagraphStore from "../../Stores/NavModules/NavDataGraph/DataGraphStore";

const EntitlementType = baseServiceApi.rayData["EntitlementType"];
const { ActionTypes } = AiInsightsConstants;

function getModifiedCategories(categories) {
  return categories?.map((cat) => {
    const catName = Object.keys(cat)[0];
    const subcats = cat[catName];

    const modifiedSubCategories = subcats.map((sc) => ({
      ...sc,
      isDisabled: !(sc.isAvailable && UserInfoUtil.IsUserEntitled(sc.entitlement)),
    }));

    return {
      [catName]: modifiedSubCategories,
    };
  });
}

function isSubCategoryEnabled(categories, tabAiInsightsSettings) {
  const { selectedCategory, selectedSubCategoryId } = tabAiInsightsSettings;

  const category = categories.find((cat) => Object.keys(cat)[0] === selectedCategory);
  if (!category) return false;

  const subcategories = category[selectedCategory];
  const subcategory = subcategories.find((item) => item.id === selectedSubCategoryId);

  return subcategory ? !subcategory.isDisabled : false;
}

function getFirstEnabledCategory(categories) {
  for (const cat of categories) {
    const catName = Object.keys(cat)[0];
    const subcats = cat[catName];

    const firstEnabled = subcats.find((sc) => !sc.isDisabled);
    if (firstEnabled) {
      return { selectedCategory: catName, selectedSubCategoryId: firstEnabled.id, isBeta: firstEnabled.isBeta };
    }
  }

  return null;

}

function getDefaultCategory(categories) {
  for (const cat of categories) {
    const catName = Object.keys(cat)[0];
    const subcats = cat[catName];

    const defaultCategory = subcats.find((sc) => sc.isDefault && !sc.isDisabled);
    if (defaultCategory) {
      return { category: catName, subCategoryId: defaultCategory.id, isBeta: defaultCategory.isBeta };
    }
  }

  return null;
}


function* getAiInsightsCategories() {
  try {
    const isTabAiInsightsEntitled = UserInfoUtil.IsUserEntitled(EntitlementType.AI_INSIGHTS_Entitlement);

    if (isTabAiInsightsEntitled) {
      const response = yield call(aiInsightsApi.getAiInsightsCategories);
      if (!response.categories) {
        yield put({
          type: ActionTypes.GET_AI_INSIGHTS_CATEGORIES_SUCCESS,
          data: [],
          settings: null,
          message: "",
        });
        return;
      }
      let categories = getModifiedCategories(response.categories);

      const consoleSettings = SettingsStore.getConsoleSettings();
      const tabAiInsightsSettings = consoleSettings.NavDatagraphSettings.TabAiInsightsSettings;
      let category = null;
      let subCategoryId = null;
      let data = [];
      let isBeta = false;

      // when there is no data in settings, 
      // or if data is present in settings but not enabled for user,
      // default value is set      
      if (!(tabAiInsightsSettings.selectedSubCategoryId && tabAiInsightsSettings.selectedCategory) || !isSubCategoryEnabled(categories, tabAiInsightsSettings)) {
        const defaultCategory = getDefaultCategory(categories)
        // default is present in response
        if (defaultCategory !== null) {
          category = defaultCategory.category;
          subCategoryId = defaultCategory.subCategoryId;
          isBeta = defaultCategory.isBeta;
          data = categories
        } else {
          // if default not present, first enabled category is set
          const firstEnabledCategory = getFirstEnabledCategory(categories);
          if (firstEnabledCategory !== null) {
            category = firstEnabledCategory.selectedCategory;
            subCategoryId = firstEnabledCategory.selectedSubCategoryId;
            data = categories
            isBeta = firstEnabledCategory.isBeta;
          } else {
            // when no category is enabled, no data is shown in UI
            data = []
          }
        }
      } else {
        // if data is present in settings and it is enabled for user
        data = categories
        category = tabAiInsightsSettings.selectedCategory;
        subCategoryId = tabAiInsightsSettings.selectedSubCategoryId;
        const subCategory = data.find((cat) => Object.keys(cat)[0] === category)?.[category]?.find((sc) => sc.id === subCategoryId);
        if (subCategory) {
          isBeta = subCategory.isBeta;
        } else {
          isBeta = false;
        }
      }

      tabAiInsightsSettings.selectedCategory = category;
      tabAiInsightsSettings.selectedSubCategoryId = subCategoryId;
      SettingsStore.saveSettings();
      yield put({
        type: ActionTypes.GET_AI_INSIGHTS_CATEGORIES_SUCCESS,
        data: data,
        settings: tabAiInsightsSettings,
        message: "",
        isCurrentReportBeta: isBeta,
      });
    }

  } catch (error) {
    yield put({
      type: ActionTypes.GET_AI_INSIGHTS_CATEGORIES_FAILURE,
      data: [],
      message:
        "Something went wrong while fetching the data. Please try again after some time.",
    });

    console.log(
      `%cError occurs in AiInsightsSaga.js, getAiInsightsCategories ${error}`,
      "color:#508F4E;background-color:#ffff00"
    );
  }
}


function getReportTitle(report) {
  const lines = report.split('\n');
  const line = lines[0];
  const trimmed = line.trim();
  if (trimmed.startsWith('# ')) {
    return trimmed.slice(2);
  }
  if (trimmed.startsWith('#')) {
    return trimmed.replace(/^#+\s*/, '');
  }

  return null;
}


function* onAiInsightsSelectedCategoryReportEnd() {
  const { aiInsightsSelectedReportData } = yield select(getAiInsightsStates);
  const title = getReportTitle(aiInsightsSelectedReportData)
  const now = new Date();
  yield put({
    type: ActionTypes.SET_AI_INSIGHTS_SELECTED_REPORT_TITLE,
    data: title,
    lastGeneratedTime: now
  });
  const { IsMiniListPlay, SelectedTabKey } = datagraphStore.getState()
  if (SelectedTabKey === TabType.AiInsights && IsMiniListPlay) {
    MiniListStore.setMiniListNextInterval();
  }
}


function* onAiInsightsSelectedCategoryReport(data) {
  const { aiInsightsSelectedReportData } = yield select(getAiInsightsStates);
  yield put({
    type: ActionTypes.GET_AI_INSIGHTS_SELECTED_REPORT_DATA_SUCCESS,
    data: aiInsightsSelectedReportData + data,
    message: "",
  });
}


function* onAiInsightsSelectedCategoryReportFailure() {
  yield put({
    type: ActionTypes.GET_AI_INSIGHTS_SELECTED_REPORT_DATA_FAILURE,
    data: "",
    message: "No Data Available",
  });
};


function* getAiInsightsSelectedCategoryReport(action) {
  try {
    const isTabAiInsightsEntitled = UserInfoUtil.IsUserEntitled(EntitlementType.AI_INSIGHTS_Entitlement);
    const { applicableInstrumentsAiInsights } = yield select(getAiInsightsStates)
    const { SymbolInfo } = yield select(getDatagraphStates);

    if (isTabAiInsightsEntitled && applicableInstrumentsAiInsights.includes(SymbolInfo.SymTypeEnum)) {
      const { aiInsightsSettings, aiInsightsCategories } = yield select(getAiInsightsStates)
      const category = aiInsightsSettings.selectedCategory;
      const subCategoryId = aiInsightsSettings.selectedSubCategoryId;
      const subCategory = aiInsightsCategories.find((cat) => Object.keys(cat)[0] === category)?.[category]?.find((sc) => sc.id === subCategoryId);
      const selectedSubCategoryId = aiInsightsSettings.selectedSubCategoryId
      const symbol = yield select(symbolSelect);
      const { SymbolInfo } = yield select(getDatagraphStates);
      const { SelectedTabKey } = datagraphStore.getState()
      const osid = SymbolInfo.Osid;
      const companyName = SymbolInfo.CompanyName;
      // closing previously streamed api
      baseServiceApi.closeCurrentEventSource();
      const request = new GetAiInsightsSelectedReportDataRequest(symbol, companyName, osid, selectedSubCategoryId);
      if (action.type === SettingsConstants.ActionTypes.SYMBOL_CHANGE_EVENT && SelectedTabKey === TabType.AiInsights) {
        ConsoleStore.trackUsage("SymbolChanged", "", ["AI", subCategory?.label, "AI Insights", SymbolInfo.Symbol]);
      }
      yield call(handleStreamRequest, { request, onMessage: onAiInsightsSelectedCategoryReport, onFailure: onAiInsightsSelectedCategoryReportFailure, onEnd: onAiInsightsSelectedCategoryReportEnd });
    }
  } catch (error) {
    yield put({
      type: ActionTypes.GET_AI_INSIGHTS_SELECTED_REPORT_DATA_FAILURE,
      data: "",
      message:
        "Something went wrong while fetching the data. Please try again after some time.",
    });

    console.log(
      `%cError occurs in AiInsightsSaga.js, getAiInsightsSelectedCategoryReport ${error}`,
      "color:#508F4E;background-color:#ffff00"
    );
  }
}


function createStreamChannel(request) {
  return eventChannel((emitter) => {
    const eventSource = baseServiceApi.processEventSourceServiceCall(
      request,
      (message) => {
        if (message.final_answer) {
          emitter({ type: "message", data: message.final_answer });
        }
        else if (message.event_name === "COMPLETED") {
          emitter({ type: "completed" });
        } else if (message.event_name === "ERROR") {
          emitter({ type: "error", error: message.message });
        }
      }
    );
    eventSource
      .then(() => {
        emitter({ type: "end" });
      })
      .catch((error) => {
        emitter({ type: "error", error });
      });
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  });
}


function* handleStreamRequest({ request, onMessage, onFailure, onEnd }) {
  try {
    const channel = yield call(createStreamChannel, request);
    try {
      let buffer = '';
      const MIN_LENGTH = 50;
      while (true) {
        const event = yield take(channel);
        if (event.type === "message") {
          buffer += event.data;
          if (buffer.length >= MIN_LENGTH) {
            yield call(onMessage, buffer);
            buffer = '';
          }
        } else if (event.type === "end") {
          if (buffer.length > 0) {
            yield call(onMessage, buffer);
          }
          break;
        } else if (event.type === "completed") {
          if (buffer.length > 0) {
            yield call(onMessage, buffer);
          }
          yield call(onEnd);
          break;
        }
        else if (event.type === "error") {
          yield call(onFailure);
          break;
        }
      }
    } catch (error) {
      console.log(
        `%cError occures in AiInsightsSaga.js, stream handler ${error}`
      );
    }
  } catch (error) {
    console.log(
      `%cError occures in AiInsightsSaga.js, stream handler ${error}`
    );
  }
}


function* setAITextReactions({ text, reaction, comments }) {
  try {
    const { SymbolInfo } = yield select(getDatagraphStates);
    const { aiInsightsSettings } = yield select(getAiInsightsStates);
    const selectedSubCategoryId = aiInsightsSettings.selectedSubCategoryId
    let payload = {
      OSID: SymbolInfo.Osid,
      aiReportTypeId: selectedSubCategoryId,
      reactions: [{
        feedback_type: reaction,
        comments: comments,
        feedback_description: text,
      }]
    };
    yield call(aiInsightsApi.setAITextReactions, payload);
  } catch (error) {
    console.log(
      `%cError occures in AIInsihtsNewsSaga.js, setAITextReactions ${error}`
    );
  }
}


export function* watchAiInsightsCategories() {
  yield takeLatest(
    ActionTypes.GET_AI_INSIGHTS_CATEGORIES_REQUEST,
    getAiInsightsCategories
  );
}

export function* watchAiInsightsSelectedCategory() {
  yield takeLatest(
    ActionTypes.GET_AI_INSIGHTS_SELECTED_REPORT_DATA_REQUEST,
    getAiInsightsSelectedCategoryReport
  );
}

export function* watchSymbolChange() {
  yield takeLatest(
    SettingsConstants.ActionTypes.SYMBOL_CHANGE_EVENT,
    getAiInsightsSelectedCategoryReport
  );
}

export function* watchSetAiInsightsFeedback() {
  yield takeLatest(
    ActionTypes.SET_AI_INSIGHTS_FEEDBACK,
    setAITextReactions
  );
}
